// @ts-check
'use_strict';
import { RegistrationAddressValidator, ResellerView } from '../tv_users/users';
import { init_g_recaptcha } from '../tv_third_party_integrations/g_recaptcha';
import { StickyBlock } from '../tv_utils/sticky_block';
import type { ShippingCheckoutView } from './ShippingCheckoutView';
export declare var $;

/**
 * This class handles loading and unloaded of the step_two checkout js.
 * CS Cart uses ajax to completely replace the elements on the dom when switching between
 * step one and step two. So if a user switches back and forth we need to update and re-bind the js.
 *
 * @property {MutationObserver} observer used to watch for changes to the checkout_steps div.
 * @property type {ShippingCheckoutView} view to load and unload on DOM updates.
 */
export class CheckoutViewLoader {
    observer: MutationObserver;
    view: ShippingCheckoutView;
    registration: RegistrationAddressValidator;
    reseller: ResellerView;

    /**
     * @var {HTMLElement} header the full tygh header element.
     * @var {HTMLElement} side_grid is the side grid element containing the sticky sidebar.
     * @var {HTMLElement} container is the container for the main content and the side bar
     * @var {HTMLElement} footer is the footer at the bottom of the window.
     * @var {string} mobile_use saves the original CSS Styling in case the view drops to mobile.
     * @var {string} button_margin stored original top margin value for checkout prompt container.
     * @var {HTMLElement} checkout_prompt_container element that contains place order button.
     * @var {HTMLElement} sticky_container is the wrapper placed around the sidebar blocks to add sticky functionality.
     * @var {StickyBlock} sticky_block for handling sticky sidebar behavior
     */
    header: HTMLElement;
    side_grid: HTMLElement;
    main_content: HTMLElement;
    container: HTMLElement;
    footer: HTMLElement;
    mobile_use: string = '';
    button_margin: string = '';
    checkout_prompt_container: HTMLElement;
    sticky_container: HTMLElement;
    sticky_block: StickyBlock;

    /**
     * Init the observer and provide the callback to use when a change occurs to the checkout_steps element.
     * Then run the update view for the initial page load,
     * as step two could be the active step on reload, or during navigation.
     *
     * @returns {CheckoutViewLoader} initialized.
     */
    constructor() {
        // Grab the Header and Footer to sticky the Side Bar.
        this.header = <HTMLElement>document.querySelector('.tygh-header');
        this.footer = document.getElementById('tygh_footer');
        this.side_grid = document.querySelector('.side-grid');
        this.main_content = document.querySelector('.tv-checkout-height');
        this.container = this.main_content.parentElement;
        this.checkout_prompt_container = <HTMLElement>document.querySelector('.tv-checkout-prompt-container');
        // TODO: Determine if this is needed with Unitheme2.
        // const button_style = window.getComputedStyle(this.checkout_prompt_container);
        // this.button_margin = button_style.marginTop;
        const deleteButtons = document.getElementsByClassName('ty-order-products__item-delete');

        for (const button of deleteButtons) {
            button.addEventListener('click', () => {
                $.toggleStatusBox('show', {
                    statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper"> Removing Item </span>',
                    statusClass: 'ty-ajax-loading-box_text_block'
                });
            });
        }

        // Only do this if its not in mobile.
        if (window.innerWidth > 768) {
            // TODO: Determine if this is needed with Unitheme2.
            // this.init_sticky_side_bar();
        }

        this.observer = new MutationObserver(this.update.bind(this));
        this.observer.observe(document.getElementById('checkout_steps'), { childList: true });

        this.update();
    }

    /**
     * Create sticky container and initialize StickyBlock.
     * To make the sticky side bar fully compatible with the backend layout editor,
     * the sticky container for the side grid blocks is created with JS.
     *
     * @return {void}
     */
    init_sticky_side_bar(): void {
        const side_bar_blocks = Array.from(this.side_grid.children);

        // Create a sticky container for all the sidebar children.
        this.sticky_container = document.createElement("div");
        this.sticky_container.setAttribute('data-sticky-top', '60');
        this.sticky_container.classList.add('tv-order-summary-sticky', 'js-sticky');
        this.side_grid.appendChild(this.sticky_container);

        // Move all sidebar blocks into sticky container
        for (const block of side_bar_blocks) {
            this.sticky_container.appendChild(block);
        }

        // Apply flex properties to side grid and parent
        this.container.style.display = 'flex';
        for (const child of this.container.children) {
            (<HTMLElement>child).style.flexShrink = '0';
        }

        // Initialize StickyBlock class
        this.sticky_block = new StickyBlock(this.sticky_container);
    }

    /**
     * Check the status of the step two element, and either unload the view or initialize a new one.
     *
     * @returns {void}
     */
    update(): void {
        const step_one_body = document.getElementById('step_one_body');
        const btn_containers = document.querySelectorAll('.tv-checkout-prompt-container');
        if (step_one_body && step_one_body.classList.contains('ty-step__body-active')) {
            btn_containers.forEach((container) => {
                container.classList.add('hidden');
            });
        } else {
            btn_containers.forEach((container) => {
                container.classList.remove('hidden');
            });
        }

        const step_two_body = document.getElementById('step_two_body');
        if (step_two_body && !step_two_body.classList.contains('hidden')) {
            import('./ShippingCheckoutView').then(
                module => this.view = new module.ShippingCheckoutView()
            );
            this.registration = null;
            this.reseller = null;
        } else {
            this.view = null;
            this.reseller = new ResellerView();
            this.registration = new RegistrationAddressValidator(true);
        }

        // The captchas have to be re-initialized if they were hidden when the page loaded.
        init_g_recaptcha();
    }
}
